import React, { ReactText, useEffect, useState } from "react";

import { Button, Table } from "antd";
import { ColumnsType } from "antd/es/table";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import * as H from "history";
import { useHistory } from "react-router-dom";

import { getCognitoUserData } from "../../features/auth/authSlice";
import {
  fetchCompanies,
  selectCompanies,
  selectCompaniesAreFetching,
} from "../../features/company/companySlice";
import { fetchCourses } from "../../features/courses/coursesSlice";
import {
  disableGroupsStatus,
  enableGroupsStatus,
  fetchGroups,
  IGroup,
  selectAreGroupsFetching,
  selectGroups,
  selectCreatedGroup,
  setFilterTextForGroups,
  selectFilterTextForGroups,
  selectFilterCompanyIdForGroups,
  setFilterCompanyIdForGroups,
} from "../../features/groups/groupSlice";
import { fetchPrograms } from "../../features/program/programSlice";
import { UserRole } from "../../features/users/usersSlice";
import { PAGE_SIZE } from "../../utils/constants";
import { MainLayout } from "../common/Layouts/Main";
import { Status, StatusEnum } from "../common/Status";
import { TableTools } from "../common/TableTools";
import { CompanyFilter } from "../Filters/Company";
import { useDebounceSelector } from "../utils/useDebounce";
import { analytics } from '../../services/analytics';
import { ActionSuffixes, Prefixes } from "../prefixes";

import tableStyles from "../common/table.module.scss";
import { getTranslations } from "../../features/translations/translationsUtils";

function renderOptions(
  history: H.History,
) {
  return (
    <Button
      size="large"
      onClick={() => {
        history.push(`${Prefixes.group}${ActionSuffixes.new}`)
      }}
      type="primary"
    >
      {getTranslations('GROUP_NEW')}
    </Button>
  );
}

export const Group: React.FunctionComponent = () => {
  const history = useHistory();
  const companiesList = useSelector(selectCompanies);
  const isCompaniesFetching = useSelector(selectCompaniesAreFetching);
  const areGroupFetching = useSelector(selectAreGroupsFetching);
  const list = [...useSelector(selectGroups)].map((group) => ({
    ...group,
    companyName: companiesList[group.company]?.name,
  }));
  const [selectedRowKeys, selectRowKeys] = useState<ReactText[]>([]);
  const dispatch = useDispatch();
  const filterValue = useSelector(selectFilterTextForGroups);
  const filteredInfo = useSelector(selectFilterCompanyIdForGroups);
  const debounceFilterValue = useDebounceSelector(selectFilterTextForGroups, 500);
  const [showActivationBtns, setShowActivationBtns] = useState<boolean>(false);
  const cognitoData = useSelector(getCognitoUserData);
  const role = cognitoData ? cognitoData["custom:role"] : undefined;
  const cognitoUsername = cognitoData ? cognitoData['cognito:username'] : null;
  const createdGroup = useSelector(selectCreatedGroup);

  useEffect(() => {
    if (createdGroup) {
      analytics.createGroup({
        userName: cognitoUsername,
        companyName: companiesList[createdGroup.company]?.name,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdGroup]);

  useEffect(() => {
    if (selectedRowKeys.length > 0) {
      setShowActivationBtns(true);
    } else {
      setShowActivationBtns(false);
    }
  }, [selectedRowKeys]);

  const columns: ColumnsType<IGroup> = [
    {
      title: getTranslations('LIST_NAME'),
      dataIndex: "name",
      sortDirections: ["descend", "ascend"],
      defaultSortOrder: "ascend",
      sorter: (a: IGroup, b: IGroup) => a.name.localeCompare(b.name),
      filteredValue: debounceFilterValue,
      onFilter: (value, record) =>
        record.name.toLowerCase().includes(value.toString().toLowerCase()),
      ellipsis: true,
    },
    {
      title: getTranslations('LIST_ASSIGNED_COURSES'),
      dataIndex: "courses",
      render: (courses: string[]) => courses.length,
    },
    {
      title: getTranslations('LIST_CREATION_DATE'),
      dataIndex: "createdAt",
      render: (createdAt) => moment.utc(createdAt).format("DD-MM-YYYY"),
      sortDirections: ["descend", "ascend"],
      sorter: (a: IGroup, b: IGroup) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: getTranslations('LIST_STATUS'),
      dataIndex: "status",
      render: (status) => <Status status={status} />,
      sortDirections: ["descend", "ascend"],
      sorter: (a: IGroup, b: IGroup) =>
        a.status === b.status ? 0 : a.status === StatusEnum.Enable ? 1 : -1,
    },
    {
      title: getTranslations('LIST_COMPANY'),
      dataIndex: "companyName",
      sortDirections: ["descend", "ascend"],
      filteredValue: filteredInfo ? [filteredInfo] : undefined,
      onFilter: (value, record: IGroup) => record.company === (value as string),
      sorter: (a: IGroup, b: IGroup) =>
        (a.companyName || "").localeCompare(b.companyName || ""),
      render: (value, group) => {
        return (
          <React.Fragment>
            {value}
            <div className={tableStyles.actions}>
              <Button
                type="default"
                onClick={() => {
                  history.push(`${Prefixes.group}/${group._id}${ActionSuffixes.edit}`);
                }}
              >
                {getTranslations('LIST_EDIT')}
              </Button>
            </div>
          </React.Fragment>
        );
      },
    },
  ];

  useEffect(() => {
    dispatch(fetchGroups());
    dispatch(fetchCompanies());
    dispatch(fetchPrograms());
    dispatch(fetchCourses());
  }, [dispatch]);

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys: ReactText[]) => {
      selectRowKeys(selectedRowKeys);
    },
  };
  const enableStatus = () => {
    const data = {
      ids: selectedRowKeys.map((key) => key.toString()),
    };
    dispatch(enableGroupsStatus(data));
  };

  const disableStatus = () => {
    const data = {
      ids: selectedRowKeys.map((key) => key.toString()),
    };
    dispatch(disableGroupsStatus(data));
  };
  const setFilterValue = (value: string[]) => {
    dispatch(setFilterTextForGroups(value[0] || ""));
  }

  const setFilteredInfo = (value: string) => {
    dispatch(setFilterCompanyIdForGroups(value || ""));
  };

  return (
    <MainLayout
      title={getTranslations('GROUP_TITLE')}
      rightTopOptions={renderOptions(history)}
      breadcrumbs={[getTranslations('GROUP_TITLE')]}
    >
      <TableTools
        placeholder={getTranslations('LIST_SEARCH_IN_TEURONA')}
        onSearch={(value) => setFilterValue([value])}
        onChange={(event) => setFilterValue([event.target.value])}
        onEnable={() => enableStatus()}
        onDisable={() => disableStatus()}
        value={filterValue}
        filter={
          role === UserRole.SuperAdmin ? (
            <CompanyFilter onApply={setFilteredInfo} value={filteredInfo} />
          ) : (
            undefined
          )
        }
        activationBtnsVisible={showActivationBtns}
      />
      <Table<IGroup>
        loading={isCompaniesFetching || areGroupFetching}
        className={tableStyles.table}
        rowClassName={() => tableStyles.row}
        rowKey="_id"
        rowSelection={rowSelection}
        columns={columns}
        dataSource={list}
        pagination={{ position: ["bottomRight"], pageSize: PAGE_SIZE, showSizeChanger: false }}
      />
    </MainLayout>
  );
};
